import Button from "@components/Button"
import Dialog from "@components/Dialog"
import StatusIcon from "@components/StatusIcon"
import { ExclamationTriangleIcon } from "@heroicons/react/24/outline"
import { CancelationType } from "@pages/helpCenter/CancelationPage"
import { useTranslation } from "react-i18next"
import { Link } from "react-router-dom"
import { RevenueCatSubscription } from "src/types"

interface Props {
  cancelSubscriptions: (cancelationType: CancelationType) => Promise<void>
  cancelableSubscriptions: RevenueCatSubscription[]
  isSubmitting: CancelationType | null
  onClose: () => void
  open: boolean
  setIsSubmitting: (isSubmitting: CancelationType | null) => void
}

export default function CancelMultipleSubscriptionsDialog({
  cancelSubscriptions,
  cancelableSubscriptions,
  isSubmitting,
  onClose,
  open,
  setIsSubmitting,
}: Props) {
  const { t } = useTranslation()

  const handleCancelAllSubscriptions = async () => {
    setIsSubmitting("all")
    await cancelSubscriptions("all")
    setIsSubmitting(null)
  }

  const handleCancelWebSubscriptions = async () => {
    setIsSubmitting("web")
    await cancelSubscriptions("web")
    setIsSubmitting(null)
  }

  const hasAppleSubscription = cancelableSubscriptions.some((subscription) =>
    ["app_store", "mac_app_store"].includes(subscription.store),
  )

  const hasGoogleSubscription = cancelableSubscriptions.some(
    (subscription) => subscription.store === "play_store",
  )

  return (
    <Dialog onClose={onClose} open={open}>
      <div className="flex h-full flex-col items-center justify-center gap-5 text-center">
        <StatusIcon icon={ExclamationTriangleIcon} tone="yellow" />

        <div className="w-full space-y-3">
          <p className="vm-heading">
            {t("pages.helpCenter.cancelation.multipleActiveSubscriptions")}
          </p>

          <p className="vm-subheading pb-2">
            {t("pages.helpCenter.cancelation.whichOneToCancel")}
          </p>

          <Button
            onClick={handleCancelWebSubscriptions}
            submitting={isSubmitting === "web"}
            title="WEB"
            variant="primary"
          />

          {hasAppleSubscription && (
            <Link
              className="vm-button-secondary"
              to="/help-center/cancelation/apple"
            >
              Apple
            </Link>
          )}

          {hasGoogleSubscription && (
            <Link
              className="vm-button-secondary"
              to="/help-center/cancelation/google"
            >
              Google
            </Link>
          )}

          <Button
            onClick={handleCancelAllSubscriptions}
            submitting={isSubmitting === "all"}
            title={t("pages.helpCenter.cancelation.all")}
            variant="secondary"
          />
        </div>
      </div>
    </Dialog>
  )
}
